import { useState } from "react";

interface ValuationSegment {
  label: string;
  value: number;
  percentage: number;
  color: string;
}

interface DonutChartProps {
  segments: ValuationSegment[];
  openMarketValue: number;
  currency?: string;
}

const OUTER_RADIUS = 30;
const INNER_RADIUS = OUTER_RADIUS - 10; // Matches STROKE_WIDTH = 18
const STROKE_WIDTH = 18;
const HOVER_INNER_RADIUS = INNER_RADIUS - 4; // Extra padding outward/inward for easier hover
const HOVER_OUTER_RADIUS = OUTER_RADIUS + 4;
const CIRCUMFERENCE = 2 * Math.PI * OUTER_RADIUS;

export default function DonutChart({ segments, openMarketValue, currency = "RWF" }: DonutChartProps) {
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    label: string;
    value: number;
    percentage: number;
    x: number;
    y: number;
  }>({
    visible: false,
    label: "",
    value: 0,
    percentage: 0,
    x: 0,
    y: 0,
  });

  const totalValue = segments.reduce((acc, s) => acc + s.value, 0);

  const formatCurrency = (value: number) =>
    `${currency} ${Number(value || 0).toLocaleString()}`;

  const showTooltip = (
    segment: ValuationSegment,
    e: React.MouseEvent<SVGPathElement>
  ) => {
    setTooltip({
      visible: true,
      label: segment.label,
      value: segment.value,
      percentage: Math.round(segment.percentage * 10) / 10,
      x: e.clientX + 12,
      y: e.clientY - 12,
    });
  };

  const moveTooltip = (e: React.MouseEvent<SVGSVGElement>) => {
    if (tooltip.visible) {
      setTooltip((t) => ({
        ...t,
        x: e.clientX + 12,
        y: e.clientY - 12,
      }));
    }
  };

  const hideTooltip = () => {
    setTooltip((t) => ({ ...t, visible: false }));
  };

  // Helper to create donut path
  const createDonutPath = (innerR: number, outerR: number, startAngle: number, endAngle: number) => {
    const startOuter = polarToCartesian(outerR, startAngle);
    const endOuter = polarToCartesian(outerR, endAngle);
    const startInner = polarToCartesian(innerR, endAngle);
    const endInner = polarToCartesian(innerR, startAngle);

    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

    return [
      `M ${startOuter.x} ${startOuter.y}`,
      `A ${outerR} ${outerR} 0 ${largeArcFlag} 1 ${endOuter.x} ${endOuter.y}`,
      `L ${startInner.x} ${startInner.y}`,
      `A ${innerR} ${innerR} 0 ${largeArcFlag} 0 ${endInner.x} ${endInner.y}`,
      "Z",
    ].join(" ");
  };

  const polarToCartesian = (radius: number, angleInDegrees: number) => {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
    return {
      x: 50 + radius * Math.cos(angleInRadians),
      y: 50 + radius * Math.sin(angleInRadians),
    };
  };

  let accumulatedPercentage = 0;

   return (
    <div className="relative w-full h-full">
      {/* Centered text overlay using Tailwind */}
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <div className="text-center">
          <div className="text-lg md:text-xl font-bold text-gray-900">
            Total Value
          </div>
          <div className="text-lg md:text-xl font-bold text-gray-900 mt-1">
            {formatCurrency(openMarketValue)}
          </div>
          <div className="text-xs text-gray-500 mt-2">
            {/* {segments.length} components */}
          </div>
        </div>
      </div>

      <svg
        viewBox="0 0 100 100"
        className="w-full h-full"
        onMouseMove={moveTooltip}
        onMouseLeave={hideTooltip}
      >
        <g transform="rotate(-90 50 50)">
          {segments.map((segment) => {
            const startAngle = (accumulatedPercentage / 100) * 360;
            const endAngle = ((accumulatedPercentage + segment.percentage) / 100) * 360;
            accumulatedPercentage += segment.percentage;

            const visiblePath = createDonutPath(INNER_RADIUS, OUTER_RADIUS, startAngle, endAngle);
            const hoverPath = createDonutPath(HOVER_INNER_RADIUS, HOVER_OUTER_RADIUS, startAngle, endAngle);

            return (
              <g key={segment.label}>
                {/* Visible colored segment */}
                <path
                  d={visiblePath}
                  fill={segment.color}
                  opacity="0.9"
                  className="transition-all duration-200 hover:opacity-100"
                />

                {/* Invisible hover area */}
                <path
                  d={hoverPath}
                  fill="transparent"
                  className="cursor-pointer hover:fill-gray-200 hover:fill-opacity-10"
                  onMouseEnter={(e) => showTooltip(segment, e)}
                  onMouseLeave={hideTooltip}
                />
              </g>
            );
          })}
        </g>
        
        {/* REMOVE this SVG text element:
        <text
          x="50"
          y="50"
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="5"
          fontWeight="bold"
          fill="#111827"
        >
          Total Value {formatCurrency(openMarketValue)}
        </text>
        */}
      </svg>

      {/* FIXED Tooltip positioning */}
      {tooltip.visible && (
        <div
          className="fixed z-50 pointer-events-none rounded-lg bg-gray-900 px-3 py-2 text-sm text-white shadow-lg"
          style={{
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            transform: "translate(-50%, -100%)",
          }}
        >
          <div className="font-semibold">{tooltip.label}</div>
          <div className="opacity-90">
            {formatCurrency(tooltip.value)} ({tooltip.percentage}%)
          </div>
          <div className="absolute left-1/2 bottom-0 h-2 w-2 -translate-x-1/2 translate-y-1/2 rotate-45 bg-gray-900" />
        </div>
      )}
    </div>
  );
};

